// src/components/looker/LookerTemplateAccessPanel.tsx
/**
 * Master only: which templates the sidebar workspace has on /looker. The
 * default is always on (locked); any other template in the template folder can
 * be granted or taken away, or made the default for every workspace. Rendered
 * outside the control band so it stays reachable when a workspace has none.
 *
 * @module components/looker/LookerTemplateAccessPanel
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchLookerTemplates } from '@/services/looker/lookerApi';
import {
  fetchWorkspaceTemplateAccess, grantLookerTemplate, revokeLookerTemplate, setDefaultLookerTemplate,
  type WorkspaceTemplateAccess,
} from '@/services/looker/lookerTemplateAccess';
import type { LookerTemplateSummary } from '@/services/looker/lookerTypes';

interface Props {
  workspaceId: string;
  /** Called after any change, so the page reloads its template list. */
  onChanged: () => void;
}

const SMALL_BUTTON =
  'inline-flex h-7 items-center rounded-md border border-neutral-300 bg-white px-2 text-xs font-medium text-ink-900 hover:bg-neutral-100 disabled:cursor-not-allowed disabled:opacity-50';

export const LookerTemplateAccessPanel: React.FC<Props> = ({ workspaceId, onChanged }) => {
  const { t } = useTranslation();
  const [all, setAll] = useState<LookerTemplateSummary[] | null>(null);
  const [access, setAccess] = useState<WorkspaceTemplateAccess | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void fetchLookerTemplates({ kind: 'all' }).then((r) => {
      if (!cancelled) setAll(r.ok ? r.data : null);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAccess(null);
    setError(null);
    void fetchWorkspaceTemplateAccess(workspaceId).then((r) => {
      if (cancelled) return;
      if (!r.ok) return setError('looker.access.readFailed');
      setAccess(r.data);
    });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const run = async (write: () => Promise<boolean>, next: (a: WorkspaceTemplateAccess) => WorkspaceTemplateAccess) => {
    if (!access) return;
    setSaving(true);
    setError(null);
    const ok = await write();
    setSaving(false);
    if (!ok) return setError('looker.access.saveFailed');
    setAccess(next(access));
    onChanged();
  };

  const toggle = (id: string, on: boolean) =>
    run(
      () => (on ? grantLookerTemplate(workspaceId, id) : revokeLookerTemplate(workspaceId, id)),
      (a) => ({ ...a, granted: on ? [...a.granted, id] : a.granted.filter((g) => g !== id) }),
    );
  const makeDefault = (id: string) => run(() => setDefaultLookerTemplate(id), (a) => ({ ...a, defaultId: id }));

  const templates = all ?? [];
  const defaultMissing = access !== null && all !== null && !templates.some((tp) => tp.id === access.defaultId);
  const count = access ? templates.filter((tp) => tp.id === access.defaultId || access.granted.includes(tp.id)).length : 0;

  return (
    <details data-looker-access className="print:hidden rounded-2xl border border-dashed border-neutral-300 bg-white px-5 py-3">
      <summary className="cursor-pointer text-sm font-semibold text-ink-900">
        {t('looker.access.title')}
        {access && all && <span className="ml-2 font-normal text-neutral-800">{t('looker.access.count', { count, total: templates.length })}</span>}
      </summary>
      <p className="mt-2 text-sm text-neutral-800">{t('looker.access.hint')}</p>
      {defaultMissing && <p role="alert" className="mt-2 text-sm font-medium text-error-700">{t('looker.access.noDefault')}</p>}
      {error && <p role="alert" className="mt-2 text-sm font-medium text-error-700">{t(error)}</p>}
      {access && all && (
        <ul className="mt-3 divide-y divide-neutral-200">
          {templates.map((tp) => {
            const isDefault = tp.id === access.defaultId;
            return (
              <li key={tp.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <label className="inline-flex min-w-0 items-center gap-2 text-sm text-ink-900">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-neutral-400 accent-ink-950"
                    checked={isDefault || access.granted.includes(tp.id)}
                    disabled={isDefault || saving}
                    onChange={(e) => void toggle(tp.id, e.target.checked)}
                  />
                  <span className="truncate">{tp.title}</span>
                </label>
                {isDefault ? (
                  <span className="rounded-full bg-primary-500 px-2 py-0.5 text-xs font-semibold text-ink-950">{t('looker.access.default')}</span>
                ) : (
                  <button type="button" className={SMALL_BUTTON} disabled={saving} onClick={() => void makeDefault(tp.id)}>
                    {t('looker.access.makeDefault')}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </details>
  );
};
